import { env } from "./runtimeEnv";

/**
 * Locales the admin can author translations for.
 *
 * The backend exposes no endpoint listing supported locales, so the set is
 * configured per deploy target via `REACT_APP_SUPPORTED_LOCALES`.
 *
 * Format: comma-separated locale codes, each optionally `code:Label`.
 *   REACT_APP_SUPPORTED_LOCALES=en,et,ru
 *   REACT_APP_SUPPORTED_LOCALES=en:English,et:Eesti,ru:Русский
 *
 * The **first** entry is the default locale: the value stored on the entity's
 * own columns (`name`, `description`, ...) rather than in its `translations`
 * array, and what the API falls back to when a requested locale has no
 * translation.
 */
export interface SupportedLocale {
    /** BCP 47 code sent to the API as `translations[].locale`. */
    code: string;
    /** Human-readable name shown on the locale tab. */
    label: string;
    /**
     * The label as written in `REACT_APP_SUPPORTED_LOCALES`, when the entry
     * carried one. Absent for a bare `en`-style entry, whose `label` was
     * derived — and can therefore be re-derived in the interface language; see
     * `i18n/contentLocaleLabel.ts`.
     */
    configuredLabel?: string;
}

export const DEFAULT_SUPPORTED_LOCALES = "en,et,ru";

/** Resolves an English display name for a locale code, e.g. `et` -> `Estonian`. */
function displayName(code: string): string {
    try {
        const resolved = new Intl.DisplayNames(["en"], { type: "language" }).of(code);
        if (resolved != null && resolved.toLowerCase() !== code.toLowerCase()) {
            return resolved;
        }
    } catch {
        // Intl.DisplayNames unavailable, or an unrecognised code — fall through.
    }
    return code.toUpperCase();
}

export function parseSupportedLocales(raw: string | undefined): SupportedLocale[] {
    const parsed = (raw ?? DEFAULT_SUPPORTED_LOCALES)
        .split(",")
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0)
        .map(entry => {
            const separator = entry.indexOf(":");
            const code = (separator === -1 ? entry : entry.slice(0, separator)).trim();
            const label = separator === -1 ? "" : entry.slice(separator + 1).trim();
            return label.length > 0
                ? { code, label, configuredLabel: label }
                : { code, label: displayName(code) };
        })
        .filter(locale => locale.code.length > 0);

    // Duplicate codes would render duplicate tabs and produce ambiguous payloads.
    const unique = parsed.filter(
        (locale, index) => parsed.findIndex(other => other.code === locale.code) === index
    );

    return unique.length > 0 ? unique : parseSupportedLocales(DEFAULT_SUPPORTED_LOCALES);
}

export const supportedLocales: SupportedLocale[] = parseSupportedLocales(
    env("REACT_APP_SUPPORTED_LOCALES")
);

/** The locale held by the entity's own columns; never stored in `translations`. */
export const defaultLocale: SupportedLocale = supportedLocales[0];

/** Locales authored through the `translations` array. */
export const translationLocales: SupportedLocale[] = supportedLocales.slice(1);

/**
 * Whether this deploy target authors translations at all.
 *
 * False when only the default locale is configured — the case for the
 * dev/stage/demo backends, which have no `translations` support. The locale
 * tabs and the translation-coverage columns collapse away rather than offering
 * edits that would never persist.
 */
export const localizationEnabled: boolean = translationLocales.length > 0;

export function localeLabel(code: string | null | undefined): string {
    if (code == null) return "";
    return supportedLocales.find(locale => locale.code === code)?.label ?? code;
}
